import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { catchError, EMPTY, map, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { AncillaryUpsellNode, PortalOrderNode } from '@app/services/api/api.types';
import { PortalRoutes } from '@app/app-routing.module';
import {
  hasDisruptionProtection,
  hasSequentialAncillaries,
  resolveInitialAncillaryUpsellRoute,
} from '@app/pages/ancillary-upsell-page/ancillary-upsell-navigation.util';
import { ApiService } from '@app/services/api/api.service';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { StateService } from '@app/services/state/state.service';

@Component({
  selector: 'ancillary-upsell-page',
  templateUrl: './ancillary-upsell-page.component.html',
  styleUrl: './ancillary-upsell-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AncillaryUpsellPageComponent implements OnInit {
  ancillaryUpsellNode$: Observable<AncillaryUpsellNode | null>;

  activatedRoute = inject(ActivatedRoute);
  apiService = inject(ApiService);
  portalOrderService = inject(PortalOrderService);
  stateService = inject(StateService);
  router = inject(Router);

  errorState: boolean;

  ngOnInit() {
    Tracking.track(PortalTrackingEvents.ancillaryUpsellPageOpened);

    this.getAncillaryUpsellNode();
  }

  getAncillaryUpsellNode() {
    const tripPortalLink =
      this.activatedRoute.firstChild?.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam) ||
      '';

    if (!tripPortalLink) {
      void this.router.navigate([PortalRoutes.NotFound]);
    }

    const perpetual = this.activatedRoute.snapshot.queryParamMap.get('perpetual') === 'true';

    this.stateService.patchState({ perpetual });

    this.ancillaryUpsellNode$ = this.apiService
      .portalTripAncillaryUpsell({ tripPortalLink, perpetual })
      .pipe(
        map(({ data }) => {
          const upsell = data.portalTripAncillaryUpsell;

          if (upsell) {
            this.setPortalOrder(upsell);

            if (perpetual) {
              this.navigateToGratuity(tripPortalLink);
            } else {
              this.navigateByBusinessLogic(upsell, tripPortalLink);
            }
          } else {
            this.handleErrorNavigation();
          }

          return upsell;
        }),
        catchError((error: Error) => {
          console.error(error?.message || error);
          return EMPTY;
        }),
      );
  }

  handleErrorNavigation() {
    this.errorState = true;
    void this.router.navigate([PortalRoutes.AncillaryUpsell, PortalRoutes.Error]);
  }

  setPortalOrder(upsell: unknown) {
    this.portalOrderService.setPortalOrder(upsell as PortalOrderNode);
  }

  navigateToGratuity(tripPortalLink: string) {
    void this.router.navigate(
      [PortalRoutes.AncillaryUpsell, tripPortalLink, PortalRoutes.Gratuity],
      { skipLocationChange: true },
    );
  }

  navigateByBusinessLogic(upsell: AncillaryUpsellNode, tripPortalLink: string) {
    const route = resolveInitialAncillaryUpsellRoute(
      upsell,
      hasSequentialAncillaries(upsell),
      hasDisruptionProtection(upsell),
    );

    void this.router.navigate([PortalRoutes.AncillaryUpsell, tripPortalLink, route], {
      skipLocationChange: true,
    });
  }
}
